"use client";

import { useSetAtom, useAtomValue } from "jotai";
import { useNavigate, useSearchParams, useLocation } from "@remix-run/react";
import {
	regionAtom,
	regionModalAtom,
	allowedRegionAtom,
} from "~/lib/jotai/region";
import { Regions, getAllowedRegion, isSingleRegionMode } from "~/models/region";
import { Region, RegionCode } from "~/types";
import { LocalStorageKey } from "~/classes/literals";
import { useEffect } from "react";
import { isLoadingAtom } from "~/lib/jotai/loading";
import { Button } from "./ui/button";
import { getItemWithExpiry, setItemWithExpiry } from "~/lib/utils";
import { REGION_TTL } from "~/models/generalConstants";

export const RegionModal = () => {
	const navigate = useNavigate();
	const setRegion = useSetAtom(regionAtom);
	const setShowRegionModal = useSetAtom(regionModalAtom);
	const setPageLoading = useSetAtom(isLoadingAtom);
	const [searchParams] = useSearchParams();
	const location = useLocation();
	const u = searchParams.get("u");

	const allowedRegion = useAtomValue(allowedRegionAtom);

	useEffect(() => {
		// If single region mode, set region and close modal
		if (isSingleRegionMode(allowedRegion)) {
			const allowed = getAllowedRegion();
			if (allowed) {
				setRegion(allowed);
				setShowRegionModal(false);
				return;
			}
		}

		const storedCountry = getItemWithExpiry(LocalStorageKey.Country);
		if (storedCountry !== null && storedCountry in Regions) {
			const country = storedCountry as Region;
			setRegion(Regions[country].code);
		}
	}, [allowedRegion]);

	// Don't render modal if single region mode is active
	if (isSingleRegionMode(allowedRegion)) {
		return null;
	}

	const handleRegionChange = (value: RegionCode) => {
		if (location.pathname != "/") setPageLoading(true);

		setRegion(value);

		if (value === Regions.Barbados.code)
			setItemWithExpiry(
				LocalStorageKey.Country,
				Regions.Barbados.name,
				REGION_TTL
			);
		else if (value === Regions.Jamaica.code)
			setItemWithExpiry(
				LocalStorageKey.Country,
				Regions.Jamaica.name,
				REGION_TTL
			);

		if (u) navigate(`/?u=${u}`);
		else navigate("/");

		setShowRegionModal(false);
	};

	return (
		<div className="flex fixed z-50 top-0 left-0 m-0 p-0 w-[100%] h-[100%] justify-center items-center">
			<div className="transition-opacity fixed top-0 left-0 m-0 p-0 w-[100%] h-[100%] bg-white/70 backdrop-blur-[3px] z-[9]"></div>

			<div className="w-[90%] max-w-[400px] p-4 rounded-md absolute bg-white z-10 text-white overflow-clip shadow-[0_8px_30px_rgb(0,0,0,0.12)] border-thin border-t-0">
				<div className="rounded-full absolute w-[115%] h-[115%] bg-companyRed m-auto bottom-[25%] right-[3%] min-[400px]:w-[105%] min-[400px]:h-[150%] min-[400px]:bottom-[10%] min-[400px]:right-[5%]"></div>

				<div className="flex relative flex-col gap-4 z-[11]">
					<div className="mb-5">
						<h3 className="font-bold text-lg">
							Welcome to the Claims Portal
						</h3>
						<p>Please select your region before proceeding.</p>
					</div>

					<Button
						value={Regions.Barbados.code}
						onClick={(e) =>
							handleRegionChange(e.currentTarget.value as RegionCode)
						}
						className="flex gap-2 items-center p-6 py-8 bg-gradient-to-r from-primary via-primary to-primary hover:to-blue-700">
						<div className="flex items-center">
							<img
								src="/bb-flag-icon.png"
								alt=""
								className="w-12 h-8 object-cover"
							/>
						</div>
						<p className="font-medium">{Regions.Barbados.name}</p>
					</Button>

					<Button
						value={Regions.Jamaica.code}
						onClick={(e) =>
							handleRegionChange(e.currentTarget.value as RegionCode)
						}
						className="flex gap-2 items-center p-6 py-8 bg-gradient-to-r from-primary via-primary to-primary hover:to-green-700">
						<div className="flex items-center w-12 h-8 overflow-hidden">
							<img
								src="/jm-flag-icon.png"
								alt=""
								className="w-12 h-8 object-cover"
							/>
						</div>
						<p className="font-medium">{Regions.Jamaica.name}</p>
					</Button>
				</div>
			</div>
		</div>
	);
};
